import React, { useState } from 'react';
import { ArrowRightOnRectangleIcon, ClockIcon, GlobeAltIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/app/providers/AuthProvider';
import { useMaintenance } from '@/app/providers/MaintenanceProvider';
import { MaintenanceCompanionMessage } from '@/components/maintenance/MaintenanceCompanionMessage';

function formatRestoreAt(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('it-IT', { dateStyle: 'long', timeStyle: 'short' });
}

const MaintenancePage: React.FC = () => {
  const { state } = useMaintenance();
  const { logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const global = state.global;
  const restoreAt = formatRestoreAt(global.estimatedRestoreAt);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <main className="relative isolate grid min-h-[100dvh] place-items-center overflow-hidden bg-zinc-100 px-4 py-8 text-zinc-950 dark:bg-[#0b0d10] dark:text-white sm:px-8">
      <img
        src="/home-background.jpg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-[0.06] dark:opacity-[0.18]"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-zinc-100/95 via-zinc-100/92 to-slate-200/90 dark:from-[#0b0d10]/96 dark:via-[#0e1114]/94 dark:to-[#111820]/90" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_12%,rgba(0,179,188,0.10),transparent_32%)] dark:bg-[radial-gradient(circle_at_14%_12%,rgba(0,179,188,0.12),transparent_32%)]" />

      <div className="relative w-full max-w-5xl">
        <section
          className="grid overflow-hidden rounded-[1.75rem] border border-zinc-950/[0.08] bg-white/92 shadow-[0_28px_90px_rgba(15,23,42,0.12)] md:grid-cols-[minmax(0,1fr)_18rem] dark:border-white/10 dark:bg-[#15191d]/95 dark:shadow-[0_28px_90px_rgba(0,0,0,0.30)]"
          aria-labelledby="global-maintenance-title"
          aria-describedby="global-maintenance-message global-maintenance-guidance"
        >
          <div className="p-6 sm:p-9 lg:p-11">
            <div className="flex items-center gap-3">
              <img src="/logo_simbolo.png" alt="" aria-hidden="true" className="h-10 w-10 shrink-0 object-contain" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-800 dark:text-white/85">Nexus</p>
                <p className="mt-1 text-sm text-zinc-500 dark:text-white/60">Piattaforma operativa</p>
              </div>
            </div>

            <div role="status" className="mt-8 inline-flex min-h-9 items-center gap-2 rounded-full border border-slate-300/80 bg-slate-50 px-3.5 py-2 text-sm font-semibold text-slate-700 dark:border-slate-300/20 dark:bg-slate-300/[0.07] dark:text-slate-200">
              <WrenchScrewdriverIcon className="h-4 w-4" aria-hidden="true" />
              <span>Manutenzione globale attiva</span>
            </div>

            <h1 id="global-maintenance-title" className="mt-5 text-3xl font-semibold tracking-[-0.04em] text-zinc-950 dark:text-white sm:text-[2.65rem] sm:leading-[1.12]">
              Nexus è temporaneamente in manutenzione
            </h1>
            <p id="global-maintenance-message" className="mt-5 max-w-[58ch] text-base leading-relaxed text-zinc-600 dark:text-white/65">
              {global.message || 'Stiamo eseguendo un intervento tecnico sulla piattaforma.'}
            </p>
            {restoreAt && (
              <div className="mt-6 flex max-w-md items-center gap-3 rounded-xl border border-zinc-950/[0.07] bg-zinc-50/90 px-4 py-3.5 dark:border-white/[0.09] dark:bg-white/[0.04]">
                <ClockIcon className="h-5 w-5 shrink-0 text-slate-600 dark:text-slate-300" aria-hidden="true" />
                <div>
                  <p className="text-xs font-medium text-zinc-500 dark:text-white/55">Ripristino previsto, indicativo</p>
                  <time dateTime={global.estimatedRestoreAt || undefined} className="mt-1 block text-sm font-semibold text-zinc-800 dark:text-white/85">
                    {restoreAt}
                  </time>
                </div>
              </div>
            )}

            <p id="global-maintenance-guidance" className="mt-6 max-w-[58ch] text-sm leading-relaxed text-zinc-500 dark:text-white/60">
              Non è necessario aggiornare la pagina. Nexus tornerà accessibile al termine dell’intervento.
            </p>

            <MaintenanceCompanionMessage context="global" />

            <div className="mt-8 border-t border-zinc-950/[0.08] pt-6 dark:border-white/[0.10]">
              <Button type="button" color="dark/zinc" className="min-h-11 px-5" onClick={handleLogout} disabled={loggingOut}>
                <ArrowRightOnRectangleIcon data-slot="icon" aria-hidden="true" />
                {loggingOut ? 'Disconnessione...' : 'Esci da Nexus'}
              </Button>
            </div>
          </div>

          <aside className="flex flex-col justify-between border-t border-zinc-950/[0.08] bg-slate-50/80 p-6 dark:border-white/[0.08] dark:bg-slate-300/[0.035] sm:p-8 md:border-l md:border-t-0" aria-label="Ambito della manutenzione globale">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-200/75 text-slate-700 ring-1 ring-inset ring-slate-300/75 dark:bg-slate-300/[0.09] dark:text-slate-200 dark:ring-slate-200/[0.12]">
                <GlobeAltIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Ambito</p>
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">Tutta la piattaforma</p>
              </div>
            </div>

            <div className="mt-8 border-t border-zinc-950/[0.08] pt-5 dark:border-white/[0.10]">
              <p className="text-xs font-medium text-zinc-500 dark:text-white/60">Stato</p>
              <p className="mt-1 text-sm font-semibold text-zinc-800 dark:text-white/85">Accesso operativo sospeso</p>
            </div>
          </aside>
        </section>

        <footer className="mt-5 text-center text-xs text-zinc-500 dark:text-white/60">
          Polo9 Società Cooperativa Sociale Impresa Sociale
        </footer>
      </div>
    </main>
  );
};

export default MaintenancePage;
